import {
  DesktopIcon,
  ExclamationTriangleIcon,
  GridIcon,
} from '@radix-ui/react-icons';
import { Flex, Text } from '@radix-ui/themes';
import { MlbStatsHelper } from 'classes/helpers/mlb-stats.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import {
  AddAveragesIcon,
  AddPitchIcon,
  RowsAddIcon,
  SuperAdminIcon,
  VoidIcon,
} from 'components/common/custom-icon/shorthands';
import { PitchDataDialog } from 'components/common/dialogs/pitch-data';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { CommonTable } from 'components/common/table';
import { CommonTooltip } from 'components/common/tooltip';
import { GenerateVideoDialog } from 'components/sections/game-data/dialogs/generate-video';
import { ViewBroadcastDialog } from 'components/sections/game-data/dialogs/view-broadcast';
import { GameDataPitchesFilters } from 'components/sections/game-data/pitches/filters';
import { GameDataPlayerAvatar } from 'components/sections/game-data/players/avatar';
import { AimingContext } from 'contexts/aiming.context';
import { GameDataContext } from 'contexts/game-data.context';
import { CheckedProvider } from 'contexts/layout/checked.context';
import { MachineContext } from 'contexts/machine.context';
import { GameDataTab } from 'enums/game-data.enums';
import { ResetPlateMode } from 'enums/machine.enums';
import { PitchDataMode } from 'enums/pitches';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableAction } from 'interfaces/tables/action';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { PlateHelper } from 'lib_ts/classes/plate.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { HitterSide } from 'lib_ts/enums/hitters.enums';
import {
  PitcherHand,
  lookupPitchType,
  tooltipPitchType,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMlbPitchExt } from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { useCallback, useContext, useMemo, useState } from 'react';
import { MlbStatsService } from 'services/mlb-stats.service';
import { useAuthStore } from 'stores/use-auth-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import useHittersStore from 'stores/use-hitters-store';
import useMiscStore from 'stores/use-misc-store';
import { useVideosStore } from 'stores/use-videos-store';

const COMPONENT_NAME = 'GameDataPitches';

const PAGE_SIZES = TABLES.PAGE_SIZES.LG;

const ENABLE_SKELETAL_DOWNLOAD = false;
const ENABLE_VIDEO_GENERATION = false;

const ERROR_ICON = <ExclamationTriangleIcon />;

const isObjectNonEmpty = (m: any, ignoreKeys?: string[]): boolean => {
  return (
    Object.keys(m)
      .filter((k) => !ignoreKeys || !ignoreKeys.includes(k))
      .filter((k) => {
        const v = m[k];

        if (v === undefined) {
          return false;
        }

        if (v === null) {
          return false;
        }

        if (typeof v === 'string') {
          // empty strings don't count
          return v.length > 0;
        }

        if (ArrayHelper.isArray(v)) {
          // empty arrays don't count
          return (v as any[]).length > 0;
        }

        return true;
      }).length > 0
  );
};

export const GameDataPitches = (props: {
  mode: GameDataTab;
  pitches: IMlbPitchExt[] | undefined;
  playerPk?: number;
}) => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const activeHitter = useHittersStore((s) => s.activeHitter);

  const { loading, filterPitches, buildPitches, openAddDialog } =
    useContext(GameDataContext);

  const isFiltering = isObjectNonEmpty(filterPitches);

  const app = useCookiesStore((s) => s.app);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { pitchBP, setAndSendForBP } = useContext(AimingContext);
  const { machine } = useContext(MachineContext);

  const [dialogBroadcast, setDialogBroadcast] = useState<number>();
  const [dialogData, setDialogData] = useState<number>();
  const [dialogVideo, setDialogVideo] = useState<number>();

  const [selectedGuid, setSelectedGuid] = useState<IMlbPitchExt>();

  const rowActions: ITableAction[] = useMemo(() => {
    const output: ITableAction[] = [
      {
        prefixIcon: <AddPitchIcon />,
        label: 'pd.add-to-pitch-list',
        invisibleFn: () => !userRoleFeatureFlags.gameDataActions,
        onClick: async (guid: IMlbPitchExt) => {
          openAddDialog({
            guids: [guid],
            pitcherPk: props.mode === 'pitchers' ? props.playerPk : undefined,
          });
        },
      },
      {
        prefixIcon: <GridIcon />,
        label: 'pl.view-pitch-data',
        onClick: async (guid: IMlbPitchExt) => {
          const built = (
            await buildPitches({
              priority: app.game_data_import_options.priority,
              pitches: [guid],
            })
          )[0];

          if (!built) {
            return;
          }

          setAndSendForBP({
            pitch: built,
            resetPlate: ResetPlateMode.Skip,
            sendConfig: undefined,
          });
          setDialogData(Date.now());
        },
      },
      {
        prefixIcon: <DesktopIcon />,
        label: 'common.watch-broadcast',
        invisibleFn: () => !userRoleFeatureFlags.gameDataActions,
        onClick: async (guid: IMlbPitchExt) => {
          setSelectedGuid(guid);
          setDialogBroadcast(Date.now());
        },
      },
      {
        prefixIcon: <VoidIcon />,
        label: 'common.generate-video',
        color: RADIX.COLOR.WARNING,
        invisibleFn: () =>
          !ENABLE_VIDEO_GENERATION || !userRoleFeatureFlags.gameDataActions,
        onClick: async (guid: IMlbPitchExt) => {
          setSelectedGuid(guid);
          setDialogVideo(Date.now());
        },
      },
      {
        prefixIcon: <VoidIcon />,
        label: 'common.skeletal-data',
        suffixIcon: <SuperAdminIcon />,
        color: RADIX.COLOR.WARNING,
        invisibleFn: () =>
          !ENABLE_SKELETAL_DOWNLOAD ||
          !userRoleFeatureFlags.controls.downloadVideo,
        onClick: async (guid: IMlbPitchExt) => {
          const data =
            await MlbStatsService.getInstance().getGamePlaySkeletalData(
              guid.gamePk,
              guid.guid
            );

          if (!data) {
            return;
          }

          MiscHelper.saveAs(
            new Blob([JSON.stringify(data, null, 2)]),
            `game-${guid.gamePk}_play-${guid.guid}_skeletal.json`
          );
        },
      },
    ];

    return output;
  }, [machine, userRoleFeatureFlags, activeHitter, buildPitches]);

  const columns = useMemo<ITypedTableColumn<IMlbPitchExt>[]>(
    () => [
      {
        label: 'common.actions',
        key: ACTIONS_KEY,
        actions: rowActions,
      },
      {
        label: '',
        key: '_issues',
        formatFn: (pitch) => {
          const errors = MlbStatsHelper.getErrors(pitch);
          if (errors.length > 0) {
            return (
              <CommonTooltip
                trigger={ERROR_ICON}
                content={
                  <p>
                    Invalid data detected:
                    <ul>
                      {errors.map((e, i) => (
                        <li key={`guid-${pitch.guid}-error-${i}`}>{e}</li>
                      ))}
                    </ul>
                  </p>
                }
              />
            );
          }

          return '';
        },
        sortRowsFn: (a, b, dir: number) => {
          const va = MlbStatsHelper.getErrors(a).length;
          const vb = MlbStatsHelper.getErrors(b).length;
          return dir * (va > vb ? 1 : -1);
        },
      },
      {
        invisible: props.mode === 'pitchers',
        label: 'common.pitcher',
        key: 'pitcher',
        formatFn: (m) => {
          return (
            <Flex gap="2" align="center">
              <GameDataPlayerAvatar
                size="1"
                playerPk={m.pitcherPk}
                name={m.pitcher}
              />
              <Text truncate>{m.pitcher}</Text>
            </Flex>
          );
        },
      },
      {
        invisible: props.mode === 'pitchers',
        label: '',
        disableSort: true,
        key: 'pitchHand',
        formatFn: (m) => {
          const isLeft = m.pitchHand === 'L';
          return (
            <PitcherHandBadge
              value={isLeft ? PitcherHand.LHP : PitcherHand.RHP}
            />
          );
        },
      },
      {
        invisible: props.mode === 'hitters',
        label: 'common.hitter',
        key: 'batter',
        formatFn: (m) => {
          return (
            <Flex gap="2" align="center">
              <GameDataPlayerAvatar
                size="1"
                playerPk={m.batterPk}
                name={m.batter}
              />
              <Text truncate>{m.batter}</Text>
            </Flex>
          );
        },
      },
      {
        invisible: props.mode === 'hitters',
        label: '',
        disableSort: true,
        key: 'batSide',
        formatFn: (m) => {
          const isLeft = m.batSide === 'L';
          return (
            <HitterSideBadge value={isLeft ? HitterSide.LHH : HitterSide.RHH} />
          );
        },
      },
      {
        label: 'common.type',
        key: 'type',
        formatFn: (pitch) => lookupPitchType(pitch.type),
        sortRowsFn: (a, b, dir) => -dir * StringHelper.safeSort(a.type, b.type),
        tooltipFn: (p) => t(tooltipPitchType(p.type)).toString(),
      },
      {
        label: 'common.inning',
        key: '_inning',
        formatFn: (pitch) => pitch.count?.inning,
        tooltipFn: (p) =>
          `At Bat: ${p.atBatNumber} ${HINT_DELIMITER} Pitch: ${p.pitchNumber}`,
        sortRowsFn: (a, b, dir) => dir * (a.timeCode < b.timeCode ? -1 : 1),
      },
      {
        label: 'common.outcome',
        key: 'outcome',
        formatFn: (pitch) => {
          return pitch.outcome;
        },
        sortRowsFn: (a, b, dir) =>
          -dir * StringHelper.safeSort(a.outcome, b.outcome),
      },
      {
        label: 'Zone',
        key: '_zone',
        align: 'center',
        formatFn: (pitch) => {
          const chars = MlbStatsHelper.convertToChars(
            pitch,
            app.game_data_import_options.priority,
            false
          );

          if (!chars.traj) {
            return '';
          }

          const plate = TrajHelper.getPlateLoc(chars.traj);
          const summary = PlateHelper.getPlateSummary(plate);

          return (
            <img
              alt="strike zone icon"
              className="StrikeZoneIcon"
              width={24}
              height={24}
              src={useMiscStore
                .getState()
                .getThemeImgPath(`plate/${summary.grid}.svg`)}
            />
          );
        },
        sortRowsFn: (pitchA, pitchB, dir: number) => {
          const [sortA, sortB] = [pitchA, pitchB].map((m) => {
            const chars = MlbStatsHelper.convertToChars(
              m,
              app.game_data_import_options.priority,
              false
            );

            return chars.traj
              ? PlateHelper.getPlateSummary(TrajHelper.getPlateLoc(chars.traj))
                  .sort
              : 0;
          });

          return -dir * (sortA < sortB ? -1 : 1);
        },
      },
      {
        label: 'common.speed',
        key: 'speed',
        subLabel: 'mph',
        align: 'right',
        formatFn: (pitch) => {
          const v = pitch.releaseData?.releaseSpeed;
          return v?.toFixed(1);
        },
        sortRowsFn: (pitchA, pitchB, dir: number) => {
          const [va, vb] = [pitchA, pitchB].map(
            (p) => p.releaseData?.releaseSpeed ?? 0
          );

          return dir * (va < vb ? 1 : -1);
        },
      },
      {
        label: 'common.spin',
        key: 'spin',
        subLabel: 'rpm',
        align: 'right',
        formatFn: (pitch) => {
          const vector = pitch.releaseData?.spinVector;
          return MlbStatsHelper.validSpinVector(vector)
            ? BallHelper.getNetSpin({
                wx: vector?.x ?? 0,
                wy: vector?.y ?? 0,
                wz: vector?.z ?? 0,
              }).toFixed(0)
            : ERROR_ICON;
        },
        sortRowsFn: (pitchA, pitchB, dir: number) => {
          const [va, vb] = [pitchA, pitchB].map((m) => {
            const vector = m.releaseData?.spinVector;
            return MlbStatsHelper.validSpinVector(vector)
              ? BallHelper.getNetSpin({
                  wx: vector?.x ?? 0,
                  wy: vector?.y ?? 0,
                  wz: vector?.z ?? 0,
                })
              : 0;
          });

          return dir * (va < vb ? 1 : -1);
        },
      },
      {
        label: 'common.v-break',
        key: 'breaks_zInches',
        subLabel: 'in',
        align: 'right',
        formatFn: (pitch) => {
          const traj = pitch.trajectoryData;
          return MlbStatsHelper.validTrajectory(traj)
            ? ((traj?.verticalBreakInduced ?? 0) * FT_TO_INCHES).toFixed(1)
            : ERROR_ICON;
        },
        sortRowsFn: (pitchA, pitchB, dir: number) => {
          const [va, vb] = [pitchA, pitchB].map(
            (m) => m.trajectoryData?.verticalBreakInduced ?? 0
          );
          return dir * (va < vb ? 1 : -1);
        },
      },
      {
        label: 'common.h-break',
        key: 'breaks_xInches',
        subLabel: 'in',
        align: 'right',
        tooltipFn: () => PitchDesignHelper.HB_TOOLTIP_TEXT,
        formatFn: (pitch) => {
          const traj = pitch.trajectoryData;
          return MlbStatsHelper.validTrajectory(traj)
            ? ((traj?.horizontalBreak ?? 0) * FT_TO_INCHES).toFixed(1)
            : ERROR_ICON;
        },
        sortRowsFn: (pitchA, pitchB, dir: number) => {
          const [va, vb] = [pitchA, pitchB].map(
            (m) => m.trajectoryData?.horizontalBreak ?? 0
          );
          return dir * (va < vb ? 1 : -1);
        },
      },
    ],
    [rowActions, props.mode, app.game_data_import_options.priority]
  );

  /** for active row selection functionality */
  const afterChangeSelected = useCallback(
    async (model: IMlbPitchExt | undefined) => {
      if (!model) {
        await setAndSendForBP({
          pitch: undefined,
          resetPlate: ResetPlateMode.Skip,
          sendConfig: undefined,
        });
        return;
      }

      if (pitchBP?.mlb_guid === model.guid) {
        // it's already selected, don't re-trigger everything
        return;
      }

      const built = (
        await buildPitches({
          priority: app.game_data_import_options.priority,
          pitches: [model],
        })
      )[0];

      if (!built) {
        return;
      }

      // if any videos were selected, keep using them
      if (pitchBP?.stretchVideoID) {
        built.stretchVideoID = pitchBP.stretchVideoID;
      }

      if (pitchBP?.windupVideoID) {
        built.windupVideoID = pitchBP.windupVideoID;
      }

      await setAndSendForBP({
        pitch: built,
        resetPlate: ResetPlateMode.PitchBackup,
        sendConfig: undefined,
      });
    },
    [pitchBP, activeHitter, machine, buildPitches]
  );

  const checkedMenuActions = useCallback(
    (checked: IMlbPitchExt[]): IMenuAction[] => {
      const output: IMenuAction[] = [
        {
          prefixIcon: <RowsAddIcon />,
          label: 'pd.add-all-selected-to-pitch-list',
          invisible: !userRoleFeatureFlags.gameDataActions,
          onClick: () =>
            openAddDialog({
              guids: checked,
              pitcherPk: props.mode === 'pitchers' ? props.playerPk : undefined,
            }),
        },
        {
          prefixIcon: <AddAveragesIcon />,
          label: 'pd.add-averages-to-pitch-list',
          invisible: !userRoleFeatureFlags.gameDataActions,
          onClick: async () => {
            const averages = MlbStatsHelper.averagePitches(checked);
            if (!averages || averages.length === 0) {
              NotifyHelper.warning({
                message_md: `There was a problem calculating average pitches. Please check your selections and try again.`,
              });
              return;
            }

            openAddDialog({
              guids: averages,
              pitcherPk: props.mode === 'pitchers' ? props.playerPk : undefined,
              isAverage: true,
            });
          },
        },
      ];

      return output;
    },
    [machine, userRoleFeatureFlags, pitchBP, buildPitches]
  );

  const data = useMemo(() => {
    // number comparison is faster for searching, but search inputs use strings
    const pitcherPks = (filterPitches.pitcherPks ?? []).map(parseInt);
    const hitterPks = (filterPitches.hitterPks ?? []).map(parseInt);

    return props.pitches
      ?.filter(
        (p) =>
          !filterPitches.gamePks ||
          filterPitches.gamePks.length === 0 ||
          filterPitches.gamePks.includes(p.gamePk)
      )
      .filter(
        (p) => pitcherPks.length === 0 || pitcherPks.includes(p.pitcherPk)
      )
      .filter((p) => hitterPks.length === 0 || hitterPks.includes(p.batterPk))
      .filter(
        (p) =>
          filterPitches.isHome === undefined ||
          // home team always pitches at the top, and hits at the bottom of innings
          (filterPitches.isHome === 'home') === p.count?.isTopInning
      )
      .filter(
        (p) =>
          !filterPitches.pitchType ||
          filterPitches.pitchType === lookupPitchType(p.type)
      )
      .filter(
        (p) =>
          !filterPitches.pitchHand || filterPitches.pitchHand === p.pitchHand
      )
      .filter(
        (p) => !filterPitches.batSide || filterPitches.batSide === p.batSide
      )
      .filter(
        (p) =>
          !filterPitches.outcomeType || filterPitches.outcomeType === p.outcome
      )
      .filter(
        (p) => !filterPitches.inning || filterPitches.inning === p.count?.inning
      );
  }, [props.pitches, filterPitches]);

  const pagination: ITablePageable = {
    paginationID: PaginationID.PitchList,
    total: data?.length ?? 0,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_inning',
      dir: -1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: afterChangeSelected,
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: checkedMenuActions,
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CheckedProvider data={data ?? []}>
        <CommonTable
          identifier="GDPitchesTable"
          dataTestID={COMPONENT_NAME}
          loading={!data || loading}
          toolbarContent={
            <GameDataPitchesFilters
              mode={props.mode}
              playerPk={props.playerPk}
              pitches={props.pitches ?? []}
            />
          }
          displayColumns={columns}
          displayData={data ?? []}
          noDataHeader="common.no-pitches-to-display"
          noDataBody={
            isFiltering ? 'common.no-matches-msg' : 'common.browse-welcome-msg'
          }
          enableListener={!anyOpenDialogs}
          {...pagination}
          {...checkable}
          {...select}
          {...sort}
          vFlex
        />
      </CheckedProvider>

      {dialogData && pitchBP && (
        <PitchDataDialog
          key={dialogData}
          identifier={`${COMPONENT_NAME}ViewDataDialog`}
          mode={PitchDataMode.GameData}
          pitch={pitchBP}
          onClose={() => setDialogData(undefined)}
        />
      )}

      {dialogBroadcast && selectedGuid && (
        <ViewBroadcastDialog
          key={dialogBroadcast}
          identifier={`${COMPONENT_NAME}ViewBroadcastDialog`}
          gamePk={selectedGuid.gamePk}
          guid={selectedGuid.guid}
          onClose={() => setDialogBroadcast(undefined)}
        />
      )}

      {dialogVideo && selectedGuid && (
        <GenerateVideoDialog
          key={dialogVideo}
          identifier={`${COMPONENT_NAME}GenerateVideoDialog`}
          getCachedPlayback={useVideosStore.getState().lazyLoadPlayback}
          guid={selectedGuid}
          onClose={() => setDialogVideo(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
